import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, CreditCard, Download } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { billingAPI } from '../api/index';
import { queryClient } from '../App';
import { Button } from '../components/Button';
import { PageHeader } from '../components/PageHeader';
import { formatDate } from '../lib/utils';

export function BillingPage() {
  const [showRazorpay, setShowRazorpay] = useState(false)
  const [razorpayOrder, setRazorpayOrder] = useState<any>(null)

  const { data: billingStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['billing-status'],
    queryFn: () => billingAPI.getStatus().then(r => r.data),
  })

  const { data: payments, isLoading: paymentsLoading } = useQuery({
    queryKey: ['payments'],
    queryFn: () => billingAPI.getPayments({ limit: 20 }).then(r => r.data.payments || []),
  })

  const createOrderMutation = useMutation({
    mutationFn: () => billingAPI.createOrder(),
    onSuccess: (response) => {
      setRazorpayOrder(response.data)
      openRazorpay(response.data)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to create order')
    },
  })

  const openRazorpay = (order: any) => {
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.async = true
    script.onload = () => {
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        handler: (response: any) => {
          toast.success('Payment successful! Updating subscription...')
          setShowRazorpay(false)
          // Refetch billing status and payments after successful payment
          queryClient.invalidateQueries({ queryKey: ['billing-status'] })
          queryClient.invalidateQueries({ queryKey: ['payments'] })
        },
        prefill: {
          email: '',
          contact: '',
        },
        modal: {
          ondismiss: () => setShowRazorpay(false),
        },
        theme: {
          color: '#F97316', // Saffron color
        },
      }
      try {
        const rzp = new (window as any).Razorpay(options)
        rzp.open()
      } catch (error) {
        toast.error('Failed to open payment gateway')
        console.error('Razorpay error:', error)
      }
    }
    script.onerror = () => {
      toast.error('Failed to load payment gateway')
    }
    document.body.appendChild(script)
  }

  if (statusLoading) {
    return (
      <div className="space-y-4">
        <div className="h-16 bg-gray-200 rounded-lg animate-pulse" />
        <div className="h-64 bg-gray-200 rounded-lg animate-pulse" />
      </div>
    )
  }

  if (!billingStatus) return null

  const progressPercentage = Math.max(0, Math.min(100, (billingStatus.days_remaining / 30) * 100))
  const isPastDue = billingStatus.subscription_status === 'past_due'
  const isSuspended = billingStatus.subscription_status === 'suspended'

  return (
    <div>
      <PageHeader title="Billing & Subscription" />

      {/* Current Plan Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
          <div className="flex-1">
            <p className="text-gray-600 text-sm mb-2">Current Plan</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{billingStatus.plan_name}</h2>
            <p className="text-xl font-semibold text-saffron mb-4">
              {billingStatus.price_display}
              <span className="text-sm text-gray-600 font-normal">/month</span>
            </p>

            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  billingStatus.subscription_status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : billingStatus.subscription_status === 'trialing'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {billingStatus.subscription_status === 'trialing' ? 'Trial Active' : 'Active'}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {billingStatus.subscription_status === 'trialing'
                  ? `Trial ends on ${formatDate(billingStatus.trial_ends_at)}`
                  : `Subscription ends on ${formatDate(billingStatus.subscription_ends_at)}`}
              </p>
            </div>
          </div>

          <div className="flex-1">
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-medium text-gray-700">Days Remaining</p>
                <p className="text-sm font-semibold text-saffron">{billingStatus.days_remaining} days</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-saffron h-full transition-all"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>

            {isPastDue && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">
                  Your subscription is past due. Please make a payment immediately.
                </p>
              </div>
            )}

            <Button
              onClick={() => createOrderMutation.mutate()}
              disabled={createOrderMutation.isPending}
              className="w-full bg-saffron hover:bg-orange-600"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Pay Now
            </Button>
          </div>
        </div>
      </div>

      {/* Payment History */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>
        </div>

        {paymentsLoading ? (
          <div className="space-y-2 p-6">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : !payments || payments.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-gray-500 p-6">
            <p>No payments yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-6">Date</th>
                  <th className="text-left py-3 px-6">Amount</th>
                  <th className="text-left py-3 px-6">Status</th>
                  <th className="text-left py-3 px-6">Txn ID</th>
                  <th className="text-right py-3 px-6">Receipt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payments.map((payment: any) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="py-3 px-6">{formatDate(payment.created_at)}</td>
                    <td className="py-3 px-6 font-medium">{payment.amount_display}</td>
                    <td className="py-3 px-6">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        payment.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : payment.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : payment.status === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {payment.status}
                      </span>
                    </td>
                    <td className="py-3 px-6 text-xs font-mono text-gray-600">{payment.gateway_payment_id || '-'}</td>
                    <td className="py-3 px-6 text-right">
                      <Button size="sm" variant="secondary">
                        <Download className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
